/**
 * # The whole menu-governance contract, written down once
 *
 * Every URL this feature calls. Nothing else under `Pages/MenuGovernance`
 * builds a path from a string literal, and nothing outside it builds one at
 * all — the same discipline `Pages/Pricing/pricingRoutes.ts` keeps, for the
 * same reason: the backend (`feat/menu-governance`, ADR-0011) is read from an
 * unmerged, unpushed sibling worktree rather than a released API, so a rename
 * on that branch is one edit here instead of a hunt through every screen.
 *
 * Read directly from `src/modules/menu-governance/` on that branch —
 * `menu-governance.controller.ts` for the routes, `dto/menu-version.dto.ts`
 * for the bodies, `menu-version.service.ts` for what each one actually does.
 * The pinned response and request shapes live in `menuGovernanceApi.ts`
 * beside the endpoints that return them; this file is paths only.
 *
 * ## What is deliberately NOT here: a `code` for every refusal
 *
 * `Pages/Pricing/pricingRoutes.ts` pins a `RESOURCE_PRICING_*` code per
 * refusal, because every pricing-list exception is thrown as an object body
 * (`{ message, code, ... }`) that `HttpExceptionFilter` passes straight
 * through. `MenuVersionService` does not do that: `MENU_VERSION_NOT_EDITABLE`,
 * `MENU_VERSION_ALREADY_EFFECTIVE` and `MENU_VERSION_PARTIAL_TRANSLATION` are
 * real exported constants in `menu-version.service.ts`, but every throw site
 * interpolates them into a plain English sentence —
 * `` `…and can no longer be edited (${MENU_VERSION_NOT_EDITABLE})` `` — instead
 * of sending `{ code: MENU_VERSION_NOT_EDITABLE }`. The filter passes through
 * any *extra key* on an object body; a string body has none to pass. So there
 * is nothing machine-readable to match against, and `lib/refusals.ts` in this
 * feature branches on **HTTP status** instead — 409 vs 422 vs 403 vs 404 — and
 * never on a code that does not arrive. Flagged for the backend: giving these
 * three constants the same `{ message, code }` shape the pricing-list module
 * already uses would let this file drop the status-only fallback.
 */

/** A tenant's menu versions. `GET`/`POST`, then `GET`/`PATCH` per id. */
export const MENU_VERSIONS = '/menu-governance/versions';

export function menuVersionPath(id: string): string {
  return `${MENU_VERSIONS}/${id}`;
}

/** Diffed as of the version's own `effectiveFrom`, never as of now. */
export function menuVersionPreviewPath(id: string): string {
  return `${menuVersionPath(id)}/preview`;
}

/** `PUT` upserts per `(branchId, itemId)`; the collection a rule is created in. */
export function menuVersionRulesPath(id: string): string {
  return `${menuVersionPath(id)}/rules`;
}

export function menuVersionRulePath(id: string, ruleId: string): string {
  return `${menuVersionRulesPath(id)}/${ruleId}`;
}

export function menuVersionSubmitPath(id: string): string {
  return `${menuVersionPath(id)}/submit`;
}

export function menuVersionApprovePath(id: string): string {
  return `${menuVersionPath(id)}/approve`;
}

export function menuVersionRejectPath(id: string): string {
  return `${menuVersionPath(id)}/reject`;
}

export function menuVersionPublishPath(id: string): string {
  return `${menuVersionPath(id)}/publish`;
}

export function menuVersionCancelPath(id: string): string {
  return `${menuVersionPath(id)}/cancel`;
}

/** The one button (ADR-0011 §7) — a forward publish, never a delete. */
export function menuVersionRollbackPath(id: string): string {
  return `${menuVersionPath(id)}/rollback`;
}

/**
 * What a branch carries, resolved as of `?asOf=` (default now). Lives on its
 * own controller server-side — `BranchAssortmentController` — because it is
 * about a branch, not a version.
 */
export function branchAssortmentPath(branchId: string): string {
  return `/menu-governance/branches/${branchId}/assortment`;
}

/** Where the rail sends someone to run this feature. */
export const MENU_GOVERNANCE_ROUTE = '/menu-governance';
